import { useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { DuplicateProductTypeMutation } from 'api/graphQL/mutations/projectElement.mutations'
import {
    copyProductType,
    setCopyProductTypeTrigger,
} from 'api/graphQL/reactiveVars/workspaceActions'
import { ActionButton } from 'components/ActionBar'
import StringInputPrompt from 'components/StringInputPrompt/StringInputPrompt'
import { DuplicateProductTypeInput } from 'models/duplicateProductTypeInput'
import { NewProjectElementNodeResult } from 'models/newProjectElementNodeResult'
import React, { ComponentProps } from 'react'
import { forbiddenPropertyCharacters } from './Common/addUtils'

const useDuplicateProductTypeButtonStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
}))

interface DuplicateProductTypeButtonProps {
    /*
     * Node currently selected in the project element tree.
     */
    selectedProjectElementNode?: ProjectElementNode

    /*
     * ID (GUID) of the active project product family.
     */
    projectProductFamilyId?: string

    /**
     * Callback for updating the element list with a new (e.g., duplicate) product type
     *
     * @param element the new product type element to insert into the project element tree
     */
    insertNewProductTypeElement?: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[]
    ) => void
    /**
     *  Props for the action button that triggers the Duplicate Product Type dialog
     */
    actionButtonProps: ComponentProps<typeof ActionButton>
}

const DuplicateProductTypeButton: React.FC<DuplicateProductTypeButtonProps> = ({
    selectedProjectElementNode,
    projectProductFamilyId,
    insertNewProductTypeElement,
    actionButtonProps,
}) => {
    const classes = useDuplicateProductTypeButtonStyles()
    const { isCopyProductTypeTriggered } = useReactiveVar(copyProductType)
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [showDuplicatePrompt, setShowDuplicatePrompt] = React.useState(false)
    const getDefaultDuplicateProductTypeName = () =>
        `Copy of ${selectedProjectElementNode?.text}`.substring(0, 100)
    const [
        duplicateProductTypeName,
        setDuplicateProductTypeName,
    ] = React.useState(getDefaultDuplicateProductTypeName())

    const [
        duplicateProductType,
        {
            loading: duplicateProductTypeLoading,
            data: duplicateProductTypeData,
        },
    ] = useMutation<
        {
            duplicateProductType: NewProjectElementNodeResult | null
        },
        { input: DuplicateProductTypeInput }
    >(DuplicateProductTypeMutation, {
        onError: (error) => {
            showGlobalSnackbar?.({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `DuplicateProductTypeError`,
                },
            })
        },
    })

    const duplicateSelectedProductType = (nameForDuplicate: string) => {
        const duplicateProductTypeInput: DuplicateProductTypeInput = {
            productTypeElementId: selectedProjectElementNode!.elementId!,
            productTypeNameForDuplicate: nameForDuplicate,
            projectElementNodeId: selectedProjectElementNode!.id!,
            projectId: selectedProjectElementNode!.projectId!,
            projectProductFamilyId: projectProductFamilyId!,
        }
        duplicateProductType({
            variables: {
                input: duplicateProductTypeInput,
            },
        })
        setShowDuplicatePrompt(false)
        setCopyProductTypeTrigger({ isCopyProductTypeTriggered: false })
    }

    React.useEffect(() => {
        // If input dialog isn't showing, reset default input value for duplicate product type name.
        // This ensures the default input value changes when a different tree node gets selected
        // or the user closes the dialog and then brings it back again.
        !showDuplicatePrompt &&
            setDuplicateProductTypeName(getDefaultDuplicateProductTypeName())
    })

    React.useEffect(() => {
        // Update element tree with nodes from the duplicate-product-type API call.
        if (
            insertNewProductTypeElement &&
            duplicateProductTypeData?.duplicateProductType?.newNode
        ) {
            insertNewProductTypeElement(
                duplicateProductTypeData.duplicateProductType.newNode,
                false,
                duplicateProductTypeData.duplicateProductType
                    .newNodesForAdditionalInstances
            )

            // Prevent repeated calls to updateElementList (fail above "if")
            duplicateProductTypeData.duplicateProductType = null
        }
    }, [duplicateProductTypeData, insertNewProductTypeElement])

    // useEffect that triggers when the copy product type button was triggered through the project bar
    React.useEffect(() => {
        if (isCopyProductTypeTriggered) {
            setShowDuplicatePrompt(true)
        }
    }, [isCopyProductTypeTriggered])

    return (
        <>
            <ActionButton
                key={'duplicateProductTypeButton'}
                {...actionButtonProps}
                onClick={() => setShowDuplicatePrompt(true)}
            />
            <StringInputPrompt
                dialogHeading="Product Type Copy"
                inputPrompt="Assign a unique name for the product type. The new product type will be available under the current assembly's family only."
                inputTextHint="Enter Product Type Name Here"
                inputText={showDuplicatePrompt ? duplicateProductTypeName : ''}
                setInputText={setDuplicateProductTypeName}
                openDialog={showDuplicatePrompt}
                okText="Create"
                maxLength={100}
                minLength={3}
                invalidCharacters={[...forbiddenPropertyCharacters, '%']}
                handleOK={(value) => {
                    duplicateSelectedProductType(value)
                }}
                handleCancel={() => {
                    setShowDuplicatePrompt(false)
                    setCopyProductTypeTrigger({
                        isCopyProductTypeTriggered: false,
                    })
                }}
            />
            <Backdrop
                className={classes.backdrop}
                open={duplicateProductTypeLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    )
}

export default DuplicateProductTypeButton
